import { getAuthRedirectUri, getEnvConfig, getLogoutUri } from '@/lib/config/env'
import { clearPkceState, consumeCodeVerifier, consumeReturnTo, consumeState, createPkceRequest } from '@/lib/auth/pkce'
import { captureIfDeletedPending, DeletedAccountPendingSignal } from '@/lib/auth/deletedAccount'
import type { SessionTokens } from '@/lib/auth/tokenStore'

// Our backend's POST /auth/oauth/callback response (camelCase). The server runs
// the code→token exchange and sets the HttpOnly tc_refresh cookie.
type OAuthCallbackResponse = {
  accessToken?: string
  idToken?: string
  refreshToken?: string
  tokenType?: string
  expiresIn?: number
}

type ExchangeResult = {
  tokens: SessionTokens
  returnTo: string
}

const inflightCodeExchanges = new Map<string, Promise<ExchangeResult>>()

// Google is the only configured Cognito identity provider, so this always
// redirects to Google's consent screen — never the generic Hosted UI login
// form. Email/password sign-in lives on our branded React /login page.
export async function startSignInFlow(returnTo: string): Promise<void> {
  const env = getEnvConfig()
  const { codeChallenge, state } = await createPkceRequest(returnTo)
  const url = new URL(`${env.cognitoDomain}/oauth2/authorize`)
  url.searchParams.set('client_id', env.userPoolClientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', 'openid email profile aws.cognito.signin.user.admin')
  url.searchParams.set('redirect_uri', getAuthRedirectUri('/auth/callback'))
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('state', state)
  url.searchParams.set('identity_provider', 'Google')
  window.location.assign(url.toString())
}

export async function exchangeCodeForTokens(code: string, returnedState: string | null): Promise<ExchangeResult> {
  const normalizedCode = String(code || '').trim()
  if (!normalizedCode) {
    throw new Error('Missing OAuth code.')
  }

  const inFlight = inflightCodeExchanges.get(normalizedCode)
  if (inFlight) return inFlight

  const exchangePromise = (async (): Promise<ExchangeResult> => {
    const expectedState = consumeState()
    if (expectedState && returnedState !== expectedState) {
      throw new Error('OAuth state mismatch.')
    }

    const verifier = consumeCodeVerifier()
    if (!verifier) {
      throw new Error('Missing PKCE code verifier.')
    }

    const env = getEnvConfig()
    // Exchange the code through OUR backend (not browser-direct to Cognito) so
    // the server sets the HttpOnly tc_refresh cookie. credentials:'include' is
    // REQUIRED for that Set-Cookie to be stored — without it the Google session
    // is lost on reload (§1.3 / finding #12). The FE keeps the returned tokens
    // in memory exactly as before; the server holds the refresh token.
    const response = await fetch(`${env.apiBaseUrl}/auth/oauth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        code: normalizedCode,
        codeVerifier: verifier,
        // MUST equal the redirect_uri used in startSignInFlow's authorize call,
        // or the PKCE code exchange fails server-side.
        redirectUri: getAuthRedirectUri('/auth/callback'),
      }),
    })

    if (!response.ok) {
      clearPkceState()
      throw new Error(`Token exchange failed with status ${response.status}`)
    }

    const payload = (await response.json()) as OAuthCallbackResponse
    // Deleted account inside its 30-day window → no session cookie; route to the
    // choice screen (Restore or New) instead of completing sign-in.
    if (captureIfDeletedPending(payload)) throw new DeletedAccountPendingSignal()
    const expiresAt =
      payload.expiresIn && Number.isFinite(payload.expiresIn)
        ? Date.now() + payload.expiresIn * 1000
        : undefined

    return {
      tokens: {
        accessToken: payload.accessToken,
        idToken: payload.idToken,
        refreshToken: payload.refreshToken,
        tokenType: payload.tokenType,
        expiresAt,
      },
      returnTo: consumeReturnTo(),
    }
  })()

  inflightCodeExchanges.set(normalizedCode, exchangePromise)
  try {
    return await exchangePromise
  } finally {
    inflightCodeExchanges.delete(normalizedCode)
  }
}

export function buildLogoutUrl(): string {
  const env = getEnvConfig()
  const url = new URL(`${env.cognitoDomain}/logout`)
  url.searchParams.set('client_id', env.userPoolClientId)
  url.searchParams.set('logout_uri', getLogoutUri('/'))
  return url.toString()
}
